import React, {useEffect, useState} from "react";
import {useDebounce} from "../../hooks/useDebounce";
import TableSection from "./TableSection";
import styles from "../../styles/users.module.scss";
import Search from "antd/es/transfer/search";
import {Button, Dropdown} from "antd";
import {
    ANNOUCEMENT_FILTER_OPTIONS,
    DISPLAY_PAGE_OPTIONS,
    formatAnnoucementStatusAsPill, TARGET_USER_GROUP_OPTIONS
} from "../../utils/announcement.util";
import {useNavigate} from "react-router-dom";
import ToggleSwitch from "../elements/toggleSwitch/ToggleSwitch";
import announcementService from "../../api/announcement-api";
import {toast} from "react-toastify";
import {formatDate} from "../../utils/formatDate";
import Spinner from "../elements/Spinner/Spinner";

const AnnoucementTable = () => {
    const [data, setData] = useState([]);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [filter, setFilter] = useState({});
    const [loading, setLoading] = useState(true);

    const navigate = useNavigate();
    const debouncedSearch = useDebounce(search, 500);

    const onSearchChange = (val) => {
        setSearch(val);
        setCurrentPage(1);
    }

    const onFilterChange = (newFilter) => {
        setFilter((prev) => {
            const updated = { ...prev, ...newFilter };
            const same = Object.keys(updated).every(
                (key) => updated[key] === prev[key]
            );
            return same ? prev : updated;
        });
        setCurrentPage(1);
    };

    const table_columns = [
        {
            key: 'name',
            label: 'Naziv',
            render: (val) => {
                return val;
            },
        },
        {
            key: 'status',
            label: 'Status',
            render: (val) => {
                return formatAnnoucementStatusAsPill(val);
            },
        },
        {
            key: 'displayInPage',
            label: 'Stranica',
            render: (val) => {
                const option = DISPLAY_PAGE_OPTIONS.find((opt) => opt.value === val);
                return option ? option.label : 'Nepoznata stranica';
            },
        },
        {
            key: 'targetUserGroup',
            label: 'Target korisnika',
            render: (val) => {
                const option = TARGET_USER_GROUP_OPTIONS.find((opt) => opt.value === val);
                return option ? option.label : 'Nepoznat target';
            },
        },
        {
            key: 'startDate',
            label: 'Start datum',
            render: (val) => {
                return formatDate(val);
            },
        },
        {
            key: 'endDate',
            label: 'Datum završetka',
            render: (val) => {
                return formatDate(val);
            },
        },
        {
            key: 'openingCounter',
            label: 'Broj prikaza',
            render: (val) => {
                return val || 0;
            },
        },
    ];

    const onStatusToggleClick = async (id, newStatus) => {
        try {
            const response = await announcementService.toggleStatus(id);

            if (!response.success && response.success !== undefined) {
                toast.error(response.message || 'Greška pri promeni statusa');
                return;
            }
            toast.success('Status je uspešno promenjen');
            handleFetchData(); // Refresh the list
        } catch (error) {
            console.error('Error toggling status:', error);
            toast.error('Greška pri promeni statusa');
        }
    };

    const onEditAnnoucementClick = async (id) => {
        navigate(`/objave/${id}`);
    }

    const onDeleteAnnoucementClick = async (id) => {
        if (!window.confirm('Da li ste sigurni da želite da obrišete ovu objavu?')) {
            return;
        }

        try {
            const response = await announcementService.delete(id);

            if (!response.success && response.success !== undefined) {
                toast.error(response.message || 'Greška pri brisanju objave');
                return;
            }

            toast.success('Objava je uspešno obrisana');
            handleFetchData(); // Refresh the list
        } catch (error) {
            console.error('Error deleting announcement:', error);
            toast.error('Greška pri brisanju objave');
        }
    }

    const actions = [
        {
            key: 'header',
            label: 'Opcije',
            disabled: true,
            className: 'dropdown-header',
        },
        {
            key: 'status-toggle',
            label: (row) => (
                <div onClick={(e) => e.stopPropagation()}>
                    <ToggleSwitch
                        checked={row.status === 'active'}
                        onChange={(checked) => onStatusToggleClick(row._id, checked)}
                        size="md"
                        label={row.status === 'active' ? 'Deaktiviraj' : 'Aktiviraj'}
                    />
                </div>
            ),
            onClick: () => {},
            className: 'toggle-switch-item',
        },
        {
            key: 'edit',
            label: 'Uredi objavu',
            icon:  <img src="/assets/icons/edit-icon.svg" alt="Edit" />,
            onClick: (annoucement) => onEditAnnoucementClick(annoucement._id),
        },
        {
            key: 'delete',
            label: 'Obriši objavu',
            icon:  <img src="/assets/icons/delete.svg" alt="Delete" />,
            onClick: (annoucement) => onDeleteAnnoucementClick(annoucement._id),
            className: 'delete',
            danger: true,
        },
    ]

    const handleFetchData = async () => {
        setLoading(true);
        try {
            const response = await announcementService.getAll(
                currentPage,
                10,
                filter.status,
                debouncedSearch
            );
            if (!response.success && response.success !== undefined) {
                toast.error(response.message || 'Greška pri učitavanju podataka');
                setData([]);
                setTotalPages(1);
                setTotalItems(0);
                return;
            }
            const responseData = response.data || response;

            setData(responseData.items || []);
            setTotalPages(responseData.totalPages || 1);
            setTotalItems(responseData.totalItems || 0);
        } catch (error) {
            console.error('Error fetching announcements:', error);
            toast.error('Greška pri učitavanju podataka');
            setData([]);
            setTotalPages(1);
            setTotalItems(0);
        } finally {
            setLoading(false);
        }
    }

    const renderTable = (
        state,
        emptyState = {
            icon: '/assets/icons/no-annoucement.svg',
            title: 'Trenutno nema objava',
        },
    ) => {
        return (
            <TableSection
                columns={table_columns}
                actions={actions}
                data={state.data}
                emptyState={emptyState}
                showPagination={state.data?.length > 0}
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                onPageChange={setCurrentPage}
                searchPlaceholder="Pretraži"
                loading={loading}
            />
        );
    };

    useEffect(() => {
        handleFetchData();
    }, [currentPage, filter.status, debouncedSearch]);

    return (
        <>
            <div className={`${styles.wrapper} users_wrapper`}>
                <div className={styles.filters}>
                    <div className={styles.filters__left}>
                        <div>
                            <Search
                                placeholder={'Pretraži'}
                                className="search-input"
                                allowClear
                                enterButton
                                onChange={(e) => onSearchChange(e.target.value)}
                                value={search}
                                width="auto"
                            />
                        </div>
                        <Dropdown
                            menu={{
                                items: ANNOUCEMENT_FILTER_OPTIONS.map((option) => ({
                                    key: option.value,
                                    label: option.label,
                                    onClick: () => {
                                        setFilter((prev) => {
                                            return {
                                                ...prev,
                                                status: option.value,
                                            };
                                        });
                                    },
                                })),
                            }}
                            trigger={['click']}
                            placement="bottomLeft"
                            className={'btn-filter-container'}
                        >
                            <Button
                                className={`btn-tertiary btn-filter ${filter.status ? 'active' : ''}`}
                            >
                                {filter.status
                                    ? `Status: ${
                                        ANNOUCEMENT_FILTER_OPTIONS.find(
                                            (option) => option.value === filter.status
                                        )?.label
                                    }`
                                    : 'Status'}
                                {filter.status && (
                                    <img
                                        src="/assets/icons/x-gray.svg"
                                        title="Ukloni filter status"
                                        style={{
                                            width: '10px',
                                            height: '10px',
                                            marginRight: '5px',
                                            cursor: 'pointer',
                                        }}
                                        alt="Ukloni filter"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setFilter((prev) => {
                                                return {...prev, status: null};
                                            });
                                        }}
                                    />
                                )}
                            </Button>
                        </Dropdown>
                    </div>
                </div>
                <>
                    {loading ? <Spinner size="lg" /> : renderTable({data})}
                </>
            </div>
        </>
    );
}

export default AnnoucementTable;